import React, { useEffect, useState, useRef } from 'react';
import { Target, Eye } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import SectionTitle from '../common/SectionTitle';
import AnimatedSection from '../ui/AnimatedSection';
import '../../styles/components/AboutTeam.css';

interface CountUpProps {
  target: number;
  suffix?: string;
  label: string;
}

const CountUp: React.FC<CountUpProps> = ({ target, suffix = '', label }) => {
  const [count, setCount] = useState(0);
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.3 });
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!isVisible || hasAnimated.current) return;
    hasAnimated.current = true;

    const duration = 1600;
    const startTime = performance.now();
    let rafId: number;

    const easeOutQuart = (t: number): number => 1 - Math.pow(1 - t, 4);

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutQuart(progress);

      setCount(Math.round(easedProgress * target));

      if (progress < 1) {
        rafId = requestAnimationFrame(animate);
      }
    };

    rafId = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(rafId);
  }, [isVisible, target]);

  return (
    <div ref={ref} className="about__stat">
      <span className="about__stat-number">{count}{suffix}</span>
      <span className="about__stat-label">{label}</span>
    </div>
  );
};

const AboutTeam: React.FC = () => {
  const { t } = useLanguage();

  return (
    <section id="about" className="section about">
      <div className="container">
        <SectionTitle title={t('about.title')} subtitle={t('about.subtitle')} />

        <AnimatedSection className="about__content">
          <p className="about__description">{t('about.description')}</p>
        </AnimatedSection>

        <div className="about__cards">
          <AnimatedSection className="about__card" animation="fade-in-left" delay={150}>
            <div className="about__card-icon">
              <Target size={28} />
            </div>
            <h3 className="about__card-title">{t('about.missionLabel')}</h3>
            <p className="about__card-text">{t('about.mission')}</p>
          </AnimatedSection>

          <AnimatedSection className="about__card" animation="fade-in-right" delay={300}>
            <div className="about__card-icon">
              <Eye size={28} />
            </div>
            <h3 className="about__card-title">{t('about.visionLabel')}</h3>
            <p className="about__card-text">{t('about.vision')}</p>
          </AnimatedSection>
        </div>

        <div className="about__stats">
          <CountUp target={5} suffix="+" label={t('about.stats.experience')} />
          <CountUp target={10} suffix="+" label={t('about.stats.projects')} />
          <CountUp target={3} label={t('about.stats.engineers')} />
        </div>
      </div>
    </section>
  );
};

export default AboutTeam;
